"use client";

/**
 * BLOCO 5 (efeito, não conteúdo) — camada de animação da landing, aditiva.
 * Traz do Mockup_Site_Seraik_v2: reveal-on-scroll (fade-up) nas seções + glow
 * ambiente da marca. NÃO altera o conteúdo do hero.tsx. Defensivo: se o JS
 * falhar, nada fica escondido (a classe reveal só é aplicada após o mount, e há
 * um failsafe que revela tudo depois de 1,8s).
 */

import { useEffect } from "react";

export function LandingEffects() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    // 1) CSS injetado uma vez
    const styleId = "seraik-landing-fx";
    if (!document.getElementById(styleId)) {
      const st = document.createElement("style");
      st.id = styleId;
      st.textContent = `
        .seraik-reveal{opacity:0;transform:translateY(28px);transition:opacity .8s cubic-bezier(.2,.7,.2,1),transform .8s cubic-bezier(.2,.7,.2,1)}
        .seraik-reveal.seraik-in{opacity:1;transform:none}
        .seraik-amb{position:fixed;inset:0;z-index:0;pointer-events:none;overflow:hidden}
        .seraik-glow{position:absolute;border-radius:50%;filter:blur(90px);opacity:.22;animation:seraikDrift 22s ease-in-out infinite}
        .seraik-g1{width:520px;height:520px;background:radial-gradient(circle,#14b4f9,transparent 70%);top:-140px;left:-80px}
        .seraik-g2{width:600px;height:600px;background:radial-gradient(circle,#d20ffd,transparent 70%);top:120px;right:-180px;animation-delay:-8s}
        .seraik-g3{width:460px;height:460px;background:radial-gradient(circle,#7b5cff,transparent 70%);top:820px;left:36%;animation-delay:-14s}
        @keyframes seraikDrift{0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(40px,-30px) scale(1.08)}66%{transform:translate(-30px,24px) scale(.94)}}
      `;
      document.head.appendChild(st);
    }

    // 2) glow ambiente (atrás de tudo)
    let amb: HTMLDivElement | null = document.querySelector(".seraik-amb");
    if (!amb) {
      amb = document.createElement("div");
      amb.className = "seraik-amb";
      amb.innerHTML = '<div class="seraik-glow seraik-g1"></div><div class="seraik-glow seraik-g2"></div><div class="seraik-glow seraik-g3"></div>';
      document.body.prepend(amb);
    }

    // 3) reveal-on-scroll: SÓ as seções ABAIXO da dobra começam ocultas e animam
    //    ao entrar na viewport. As acima da dobra ficam visíveis (sem flash, já
    //    foram vistas). Sem failsafe cego que revelava tudo e matava a animação.
    const targets = Array.from(document.querySelectorAll<HTMLElement>("section"))
      .filter((el) => el.offsetHeight > 0 && !el.closest("nav"));

    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("seraik-in"); io.unobserve(e.target); } }),
      { threshold: 0, rootMargin: "0px 0px -12% 0px" },
    );
    const fold = window.innerHeight * 0.85;
    targets.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top > fold) { el.classList.add("seraik-reveal"); io.observe(el); } // só below-fold
    });

    // 4) HERO 3D estilo Apple: tilt do mockup com o mouse + leve parallax no scroll
    const tilt = document.querySelector<HTMLElement>("[data-seraik-tilt]");
    let rafT = 0, tx = 0, ty = 0, sy = 0;
    const applyTilt = () => {
      if (!tilt) return;
      tilt.style.transform = `perspective(1200px) rotateY(${tx * 9}deg) rotateX(${-ty * 7}deg) translateY(${sy}px)`;
    };
    const onMove = (e: MouseEvent) => {
      if (!tilt) return;
      tx = e.clientX / window.innerWidth - 0.5;
      ty = e.clientY / window.innerHeight - 0.5;
      if (!rafT) rafT = requestAnimationFrame(() => { applyTilt(); rafT = 0; });
    };
    const onScrollTilt = () => {
      if (!tilt) return;
      sy = Math.min(40, window.scrollY * 0.04); // parallax sutil
      if (!rafT) rafT = requestAnimationFrame(() => { applyTilt(); rafT = 0; });
    };
    if (tilt) {
      tilt.style.transition = "transform .18s cubic-bezier(.2,.7,.2,1)";
      tilt.style.willChange = "transform";
      window.addEventListener("mousemove", onMove);
      window.addEventListener("scroll", onScrollTilt, { passive: true });
      applyTilt();
    }

    return () => { io.disconnect(); amb?.remove(); window.removeEventListener('mousemove', onMove); window.removeEventListener('scroll', onScrollTilt); if (rafT) cancelAnimationFrame(rafT); };
  }, []);

  return null;
}
